import Link from 'next/link'
import styled from 'styled-components'
import { CAD1_LIVE, CAD1_VISIBLE, cad1Paths } from 'views/Cad1/config'
import { LOCK_HOME } from 'views/LockTools/config'
import { CREATE } from 'views/TradingCompetitionV3/routes'
import type { Badge } from './logic'
import { BadgeView, Chip, DISPLAY, HUES, Hue, Icon3d, MUTED, SNOW, SNOW_2 } from './ui'

/*
 * How a project uses CADINU, in the order it happens: launch (CAD1, in testing: no link on production; the Stage build links it to /cad1,
 * still "In testing", no date), lock the liquidity, run a competition, and the
 * community trades. Four equal steps on one line from md up, a numbered column on a phone. The badges are the same live reads as the tiles.
 */
const Steps = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
  counter-reset: step;
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
  }
`
const stepBox = `
  position: relative;
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  grid-template-rows: auto auto 1fr;
  column-gap: 14px;
  align-items: start;
  height: 100%;
  min-height: 104px;
  padding: 14px;
  border-radius: 20px;
`
const StepLink = styled(Link)<{ $hue: Hue }>`
  ${stepBox}
  color: ${SNOW};
  background: linear-gradient(180deg, rgba(${({ $hue }) => HUES[$hue].rgb}, 0.12) 0%, rgba(${({ $hue }) => HUES[$hue].rgb}, 0.02) 100%), #0b1028;
  border: 1px solid rgba(${({ $hue }) => HUES[$hue].rgb}, 0.35);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 10px 26px rgba(0, 0, 0, 0.4);
  transition: border-color 150ms ease, transform 150ms ease;
  &:hover {
    border-color: rgba(${({ $hue }) => HUES[$hue].rgb}, 0.8);
    transform: translateY(-2px);
  }
  &:focus-visible {
    outline: 2px solid ${HUES.ice.text};
    outline-offset: 3px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`
const StepStatic = styled.div<{ $hue: Hue }>`
  ${stepBox}
  color: ${SNOW};
  background: rgba(${({ $hue }) => HUES[$hue].rgb}, 0.05);
  border: 1px dashed rgba(${({ $hue }) => HUES[$hue].rgb}, 0.45);
`
const Inner = styled.span`
  display: contents;
  .ico {
    grid-row: 1 / span 3;
    width: 56px;
    height: 56px;
    filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.45));
  }
  .n {
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${MUTED};
  }
  .t {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: 2px 0 4px;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 18px;
    line-height: 24px;
  }
  .d {
    font-size: 13px;
    line-height: 18px;
    color: ${SNOW_2};
  }
`

type Step = { n: number; title: string; desc: string; icon: string; hue: Hue; href?: string; badge?: Badge; chip?: string }

export function ProjectPath({ lock, compete, launch }: { lock: Badge; compete: Badge; launch: Badge }) {
  const steps: Step[] = [
    { n: 1, title: 'Launch', desc: 'Create the token with the CAD1 launcher.', icon: 'rocket', hue: 'mint', chip: CAD1_VISIBLE && CAD1_LIVE ? undefined : 'In testing', href: CAD1_VISIBLE ? cad1Paths.home : undefined, badge: launch },
    { n: 2, title: 'Lock', desc: 'Lock the liquidity. Holders can check the lock and its date.', icon: 'locked', hue: 'ice', href: LOCK_HOME, badge: lock },
    { n: 3, title: 'Compete', desc: 'Run a trading competition with a prize for the top traders.', icon: 'trophy', hue: 'amber', href: CREATE, badge: compete },
    { n: 4, title: 'Trade', desc: 'The community buys and sells on CADINUdex with Smart Swap.', icon: 'coin', hue: 'violet', href: '/swap' },
  ]
  return (
    <Steps data-testid="home-path">
      {steps.map((s) => {
        const body = (
          <Inner>
            <Icon3dBox name={s.icon} />
            <span className="n">Step {s.n}</span>
            <span className="t">
              {s.title}
              {s.chip && <Chip $hue={s.hue}>{s.chip}</Chip>}
              {s.badge && s.badge.kind !== 'none' && <BadgeView b={s.badge} />}
            </span>
            <span className="d">{s.desc}</span>
          </Inner>
        )
        return (
          <li key={s.n} data-step={s.title.toLowerCase()}>
            {s.href ? (
              <StepLink href={s.href} $hue={s.hue}>
                {body}
              </StepLink>
            ) : (
              <StepStatic $hue={s.hue} role="group" aria-label={`Step ${s.n}: ${s.title}, ${s.chip ?? ''}`}>
                {body}
              </StepStatic>
            )}
          </li>
        )
      })}
    </Steps>
  )
}

const Icon3dBox = ({ name }: { name: string }) => <Icon3d className="ico" name={name} size={56} />
